import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { usePrevious } from '@ocean/hooks'
import { Mode } from '@ocean/types/dist/appearance'
import { common } from '@ocean/ui'
import { ToastUtils, ErrorUtils as OceanErrorUtils } from '@ocean/utils'
import { SsoUtils, ErrorUtils } from 'utils'
import { useSelector } from 'hooks'
import { GlobalLoadingLayer } from 'core/layers'
import { clearSessionInfo } from 'core/session/slices/session'
import { loadSystemConfiguration } from 'core/system/slices/system'

const { Spinner } = common

type QueryParams = {
  sta?: string
  stm?: string
  sto?: string
}

// Initialization layer
// Common setup for whole Application
const InitializationLayer: React.FC = ({ children }) => {
  const dispatch = useDispatch()
  const [initializationLoading, setInitializationLoading] = useState(true)
  const systemInformationLoading = useSelector(({ system }) => system.systemInformation.isLoading)
  const prevSystemInformationLoading = usePrevious(systemInformationLoading)
  const location = useLocation()
  const queryParams: QueryParams = queryString.parse(location.search)
  const { sta, stm, sto } = queryParams

  useEffect(() => {
    // ?sta=1 - special query param for clear session data
    if (sta === '1') {
      dispatch(clearSessionInfo())
    }
    // ?stm=1 - special query param set by STA to indicate session timeout
    if (stm === '1' || sto === '1') {
      if (SsoUtils.getSsoMarker()) {
        SsoUtils.setInvalidSessionMarker()
        dispatch(clearSessionInfo())
      } else {
        const localizedError = ErrorUtils.getErrorMessage({
          code: OceanErrorUtils.INVALID_SESSION_CODE,
          message: '',
        })
        ToastUtils.warning(localizedError, OceanErrorUtils.INVALID_SESSION_CODE)
      }
    }
    dispatch(loadSystemConfiguration())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isSystemInformationLoaded = prevSystemInformationLoading && !systemInformationLoading
    if (isSystemInformationLoaded) {
      setInitializationLoading(false)
    }
  }, [systemInformationLoading, prevSystemInformationLoading])

  if (initializationLoading) return <Spinner mode={Mode.Light} />

  return <GlobalLoadingLayer>{children}</GlobalLoadingLayer>
}

export default InitializationLayer
